import React, { useEffect, useMemo, useState, useCallback } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import EditCommunityProfileModal from "../components/EditCommunityProfileModal";
import SlackJoinCard from "../components/SlackJoinCard";
import SlackThreadsStrip from "../components/SlackThreadsStrip";
import { api } from "../lib/api";
import {
  Search, Users, MapPin, ExternalLink, Instagram, Linkedin,
  Filter, X, UserPlus,
} from "lucide-react";
import { Link } from "react-router-dom";

const STAGE_LABELS = {
  "idea": "Idea",
  "pre-launch": "Pre-launch",
  "launched": "Launched",
  "in-retail": "In retail",
  "scaling": "Scaling",
};

export default function Members() {
  const [data, setData] = useState({ members: [], meta: { categories: [], stages: [] } });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [stage, setStage] = useState("");
  const [city, setCity] = useState("");

  const loadDirectory = useCallback(() => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (category) params.category = category;
    if (stage) params.stage = stage;
    if (city.trim()) params.city = city.trim();
    api.get("/directory", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ members: [], meta: { categories: [], stages: [] } }))
      .finally(() => setLoading(false));
  }, [q, category, stage, city]);

  useEffect(() => { loadDirectory(); }, [loadDirectory]);

  useEffect(() => {
    const t = setTimeout(loadDirectory, 250);
    return () => clearTimeout(t);
  }, [q, category, stage, city, loadDirectory]);

  const hasFilter = q || category || stage || city;
  const clearAll = () => { setQ(""); setCategory(""); setStage(""); setCity(""); };

  const [editOpen, setEditOpen] = useState(false);

  return (
    <AppShell title="Members">
      <div className="w-full">
        <PageHero
          eyebrow="Community"
          icon={Users}
          title={<>The <span className="text-grad">Members Directory</span></>}
          subtitle="Founders inside CPG Institute who chose to be discoverable. Reach out and swap notes with the ones you'd love to know."
          right={
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="btn-primary"
              data-testid="members-cta-edit-profile"
            >
              <UserPlus className="w-4 h-4" /> Edit your profile
            </button>
          }
        />

        <SlackJoinCard />
        <SlackThreadsStrip />

        <div className="mt-8 grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1fr_180px_180px_180px_auto] items-end" data-testid="members-filters">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, brand, bio…"
              data-testid="members-search"
              className="w-full rounded-full border border-shell-border bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-shell-accent"
            />
          </div>
          <FilterSelect label="Category" value={category} onChange={setCategory} options={data.meta.categories} testid="members-filter-category" />
          <FilterSelect label="Stage" value={stage} onChange={setStage} options={data.meta.stages} labels={STAGE_LABELS} testid="members-filter-stage" />
          <div className="relative">
            <MapPin className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
              data-testid="members-filter-city"
              className="w-full rounded-full border border-shell-border bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-shell-accent"
            />
          </div>
          {hasFilter && (
            <button
              onClick={clearAll}
              data-testid="members-filter-clear"
              className="text-xs font-semibold text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span data-testid="members-count">
            {loading ? "Loading…" : `${data.members.length.toLocaleString()} member${data.members.length === 1 ? "" : "s"}`}
          </span>
        </div>

        {!loading && data.members.length === 0 ? (
          <div className="mt-10 border border-dashed border-shell-border rounded-3xl p-10 text-center" data-testid="members-empty">
            <Users className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="mt-3 font-display text-2xl">No members yet in this view.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Try clearing filters — or be the first to publish your profile from{" "}
              <Link to="/community-profile" className="text-primary underline underline-offset-2">Community profile</Link>.
            </p>
          </div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="members-grid">
            {data.members.map((m) => (
              <MemberCard key={m.user_id} member={m} />
            ))}
          </div>
        )}
      </div>
      <EditCommunityProfileModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={loadDirectory}
      />
    </AppShell>
  );
}

/* ══════════════════ Sub-components ══════════════════ */

function FilterSelect({ label, value, onChange, options, labels, testid }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-full border border-shell-border bg-white pl-4 pr-9 py-2 text-sm focus:outline-none focus:border-shell-accent appearance-none"
      >
        <option value="">All {label.toLowerCase()}</option>
        {options.map((o) => (
          <option key={o} value={o}>{labels?.[o] || (o.charAt(0).toUpperCase() + o.slice(1))}</option>
        ))}
      </select>
      <Filter className="w-3.5 h-3.5 absolute right-3 top-3 text-muted-foreground pointer-events-none" />
    </div>
  );
}

function MemberCard({ member }) {
  const initials = (member.name || member.brand || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const hasSocial = !!(member.website || member.instagram || member.linkedin);

  return (
    <article className="card-elevated p-6 flex flex-col relative" data-testid={`member-card-${member.user_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-2xl bg-shell-side text-white grid place-items-center font-bold text-lg overflow-hidden shrink-0">
          {member.picture ? (
            <img src={member.picture} alt={member.name} className="w-full h-full object-cover" />
          ) : initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg leading-tight truncate">{member.brand || member.name || "Untitled brand"}</p>
          <p className="text-xs text-muted-foreground truncate">{member.name}</p>
          {member.city && (
            <p className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {member.city}
            </p>
          )}
        </div>
      </div>

      {(member.category || member.stage) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {member.category && <span className="chip-amber !py-0.5 !text-[10px]" data-testid={`member-cat-${member.user_id}`}>{member.category}</span>}
          {member.stage && (
            <span className="rounded-full text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 bg-shell-side text-white" data-testid={`member-stage-${member.user_id}`}>
              {STAGE_LABELS[member.stage] || member.stage}
            </span>
          )}
        </div>
      )}

      {member.bio && (
        <p className="mt-4 text-sm text-foreground/85 line-clamp-4">{member.bio}</p>
      )}

      {hasSocial && (
        <div className="mt-5 pt-4 border-t border-shell-border flex items-center gap-3 flex-wrap">
          {member.website && (
            <a href={member.website} target="_blank" rel="noreferrer" data-testid={`member-web-${member.user_id}`}
              className="text-xs font-semibold text-muted-foreground hover:text-primary inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Site
            </a>
          )}
          {member.instagram && (
            <a href={member.instagram.startsWith("http") ? member.instagram : `https://instagram.com/${member.instagram.replace(/^@/, "")}`}
              target="_blank" rel="noreferrer" data-testid={`member-ig-${member.user_id}`}
              className="text-xs font-semibold text-muted-foreground hover:text-primary inline-flex items-center gap-1">
              <Instagram className="w-3 h-3" /> IG
            </a>
          )}
          {member.linkedin && (
            <a href={member.linkedin.startsWith("http") ? member.linkedin : `https://linkedin.com/in/${member.linkedin}`}
              target="_blank" rel="noreferrer" data-testid={`member-li-${member.user_id}`}
              className="text-xs font-semibold text-muted-foreground hover:text-primary inline-flex items-center gap-1">
              <Linkedin className="w-3 h-3" /> in
            </a>
          )}
        </div>
      )}
    </article>
  );
}
